import type { SchemaOrgNode } from './schema-org-node.model';

interface SafeValue {
  changingThisBreaksApplicationSecurity?: string;
}

function isSafeValue(value: unknown): value is SafeValue {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'changingThisBreaksApplicationSecurity' in value
  );
}

function extractHtmlFromSafeValue(html: unknown): string {
  if (!html) return '';
  if (typeof html === 'string') return html;
  if (isSafeValue(html) && html.changingThisBreaksApplicationSecurity) {
    return html.changingThisBreaksApplicationSecurity;
  }
  return '';
}

export function stripHtml(html: unknown): string {
  const htmlString = extractHtmlFromSafeValue(html);
  if (!htmlString) return '';
  return htmlString
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/ ([.,!?;:])/g, '$1')
    .trim();
}

export interface FaqQuestion {
  question: unknown;
  answer: unknown;
}

export interface FaqPageSchemaData {
  '@id': string;
  questions: FaqQuestion[];
}

const QUESTION_WORD_PREFIX = /^(how|what|can|why|when|where|do|does|is|are|should|will|who|which)\b/i;

/**
 * FAQPage gating heuristic. Google's 2023 tightening of FAQ rich-result eligibility
 * means emitting FAQPage on non-FAQ content carries spam-flag risk with little upside.
 * A title counts as Q-shaped if it ends in `?` or starts with a question word.
 * Returns true only when ≥80% of stripped titles look like questions and at least
 * one item exists.
 */
export function looksLikeFaq(titles: unknown[]): boolean {
  const stripped = titles.map((t) => stripHtml(t)).filter((t) => t.length > 0);
  if (stripped.length === 0) return false;
  const questionLike = stripped.filter((t) => t.endsWith('?') || QUESTION_WORD_PREFIX.test(t));
  return questionLike.length / stripped.length >= 0.8;
}

/**
 * Builds a FAQPage Schema.org node from a list of question/answer pairs. Returns
 * null when no valid pairs (both fields non-empty after stripping) are found.
 * Each Q/A is HTML-stripped — anchor link text is preserved (only the tag and
 * href are removed) so prose using anchor text as load-bearing words still
 * reads correctly in JSON-LD output.
 */
export function buildFaqPageSchema(data: FaqPageSchemaData | null): SchemaOrgNode | null {
  if (!data || !Array.isArray(data.questions) || data.questions.length === 0) return null;

  const mainEntity = data.questions
    .map((q) => ({
      name: stripHtml(q.question),
      text: stripHtml(q.answer),
    }))
    .filter((q) => q.name && q.text)
    .map((q) => ({
      '@type': 'Question' as const,
      name: q.name,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: q.text,
      },
    }));

  if (mainEntity.length === 0) return null;

  return {
    '@type': 'FAQPage',
    '@id': data['@id'],
    mainEntity,
  };
}
